// src/utils/imageBleed.js
//
// Images that continue onto the next page.
//
// An author can let an image run past the bottom of its page instead of being
// cut off there. The editor's pages are real sheets, so a running image is
// stored the way it is shown: one cropped piece per page.
//
//   <p><span class="rv-resizable-image" data-bleed="true" data-bleed-offset="0"
//            data-bleed-id="rvb-…" style="height: 300px; overflow: hidden">
//        <img style="height: 600px; margin-top: 0px"></span></p>   ← first piece
//   ... page break ...
//   <p data-bleed-cont="true"><span ... data-bleed-offset="300"
//            data-bleed-id="rvb-…" style="height: 300px; overflow: hidden">
//        <img style="height: 600px; margin-top: -300px"></span></p> ← the rest
//
// The crop is all inline style, so every surface that shows stored pages (the
// previews, the ebook preview) draws each piece correctly without knowing
// anything about this. `data-height` on the span always holds the image's full
// height, and `data-bleed-id` ties the pieces of one image together.
//
// The first piece IS the image. Every later piece is a copy made by the page
// split, and the reflow takes the copies out and uncrops the first piece before
// it lays the pages out again, so the cut always follows the text. Print does
// the same in CSS (backend/utils/manuscript_render.py).
//
// An image with text wrapped round it (a float) runs on too, but its pieces are
// not neighbours: the text that wraps beside the first piece sits between them
// in the document. The reflow keeps filling the page beside the first piece and
// puts the rest at the top of the next page only when that page starts — see
// `reflowDocument` in EditorPage.jsx. That is why the pieces are found by id
// rather than by position.

export const BLEED_ATTR = 'data-bleed';
export const OFFSET_ATTR = 'data-bleed-offset';
export const CONT_ATTR = 'data-bleed-cont';
export const ID_ATTR = 'data-bleed-id';

// A first piece shorter than this is a sliver, not an image; move it whole.
export const MIN_PIECE_PX = 48;

const IMAGE_SEL = '.rv-resizable-image';
const ZWSP = /\u200B/g;

const imageIn = (block) =>
  block?.matches?.(IMAGE_SEL) ? block : block?.querySelector?.(IMAGE_SEL) || null;

const offsetOf = (span) => parseFloat(span.getAttribute(OFFSET_ATTR)) || 0;

// An id is issued at an image's first cut and kept while it stays cut, so a
// reflow that changes nothing saves the same HTML; `dropStaleBleedIds` takes it
// off once the image is whole again. Pasted pieces lose theirs on the way in
// (`releasePastedPieces`), which is what keeps one id to one image.
let idSeq = 0;
const newBleedId = () => `rvb-${Date.now().toString(36)}-${(idSeq += 1).toString(36)}`;

/** True for an image laid out with text wrapped round it. */
export function isWrapped(span) {
  const f = span?.style?.float;
  return f === 'left' || f === 'right';
}

/** Whether the author has let this image continue onto the next page. */
export function isBleedOn(span) {
  return span?.getAttribute?.(BLEED_ATTR) === 'true';
}

/**
 * The running image a page block holds, or null.
 *
 * Only a block that is nothing but one image qualifies — a paragraph with words
 * beside its image splits on the words, as it always has. A wrapped image is
 * normally its own block, so the words that wrap round it are not in it.
 */
export function bleedImageOf(block) {
  if (!block || (block.textContent || '').replace(ZWSP, '').trim()) return null;
  const span = imageIn(block);
  if (!span || !isBleedOn(span)) return null;
  if (block !== span && block.querySelectorAll(IMAGE_SEL).length !== 1) return null;
  return span;
}

/** The image's full height in px, whatever piece of it this span shows. */
export function fullHeight(span) {
  return parseFloat(span.getAttribute('data-height')) || span.offsetHeight || 0;
}

/** Crop `span` to `height` px of its image, starting `offset` px down it. */
export function cropPiece(span, offset, height) {
  const img = span.querySelector('img');
  span.setAttribute(OFFSET_ATTR, String(offset));
  span.style.height = `${height}px`;
  span.style.overflow = 'hidden';
  if (img) {
    img.style.height = `${fullHeight(span)}px`;
    img.style.marginTop = `${-offset}px`;
  }
}

/**
 * Undo `cropPiece`: the span shows its whole image again. Its id stays, so the
 * next cut of the same image reuses it.
 */
export function uncrop(span) {
  if (!span.hasAttribute(OFFSET_ATTR)) return;
  span.removeAttribute(OFFSET_ATTR);
  span.style.height = `${fullHeight(span)}px`;
  span.style.overflow = '';
  const img = span.querySelector('img');
  if (img) {
    img.style.height = '';
    img.style.marginTop = '';
    if (!img.getAttribute('style')?.trim()) img.removeAttribute('style');
  }
}

// Nothing in it but whitespace and line breaks.
const isBlank = (el) =>
  !(el.textContent || '').replace(ZWSP, '').trim() &&
  Array.from(el.querySelectorAll('*')).every((n) => n.tagName === 'BR');

/**
 * Put running images back together before the pages are laid out again.
 *
 * Removes every later piece (it is always a copy) and uncrops every first
 * piece. A copy's block is an ordinary editable block, so an author can type
 * into it beside the piece: a block left holding anything is kept as a plain
 * block, and only an empty one is dropped. Returns the blocks still to place,
 * in order.
 *
 * @param {Element[]} blocks page-body children in document order
 * @returns {Element[]}
 */
export function joinBleedPieces(blocks) {
  const kept = [];
  for (const block of blocks) {
    // Every piece in the block, not just the first image: editing can merge a
    // piece into a paragraph that already held another image.
    const pieces = block.matches?.(`${IMAGE_SEL}[${OFFSET_ATTR}]`)
      ? [block]
      : Array.from(block.querySelectorAll?.(`${IMAGE_SEL}[${OFFSET_ATTR}]`) || []);
    let removed = false;
    for (const piece of pieces) {
      if (offsetOf(piece) > 0) {
        if (piece !== block) piece.remove();
        removed = true;
      } else {
        uncrop(piece);
      }
    }
    if (pieces[0] === block && removed) {
      block.remove();
      continue;
    }
    if (block.hasAttribute?.(CONT_ATTR) || removed) {
      if (isBlank(block)) {
        block.remove();
        continue;
      }
      block.removeAttribute(CONT_ATTR);
    }
    kept.push(block);
  }
  return kept;
}

/**
 * Why an image cannot continue onto the next page, or null if it can.
 *
 * 'shared' — it shares its line with words or another image; the page split
 * cuts such a paragraph on its words, as it always has. A wrapped image is its
 * own block, so the words wrapping round it do not count.
 *
 * @param {Element} span a .rv-resizable-image in the editor
 * @returns {'shared'|null}
 */
export function bleedBlocker(span) {
  if (!span) return null;
  let block = span;
  while (block.parentElement && !block.parentElement.classList?.contains('rv-page-body')) {
    block = block.parentElement;
  }
  if (block === span) return null;
  const others = Array.from(block.querySelectorAll(IMAGE_SEL)).filter((s) => s !== span);
  if ((block.textContent || '').replace(ZWSP, '').trim() || others.length) return 'shared';
  return null;
}

/**
 * Split a running image at the bottom of its page.
 *
 * `block` has just been placed in `body` and made it overflow. The first piece
 * stays in `block`, cropped to what fits; the rest comes back as a new block for
 * the next page. Measured, not computed: the crop is applied and the overflow
 * that is left over taken off it, so margins, the end spacer and zoom all come
 * out right without being reasoned about. Returns null, with `block` exactly
 * as it was, when there is nothing worth splitting.
 *
 * For a wrapped image this runs before the text that wraps beside it has been
 * placed, so the overflow measured is the image's own.
 *
 * @param {Element} body the .rv-page-body `block` is in
 * @param {Element} block a direct child of `body`
 * @param {Element} span the image, from `bleedImageOf(block)`
 * @returns {Element|null} the block holding the rest of the image
 */
export function splitBleedImage(body, block, span) {
  const full = fullHeight(span);
  const offset = offsetOf(span);
  const remaining = full - offset;
  if (remaining <= 0) return null;

  const before = span.getAttribute('style');
  const img = span.querySelector('img');
  const imgBefore = img?.getAttribute('style') ?? null;
  const hadOffset = span.getAttribute(OFFSET_ATTR);
  const hadId = span.getAttribute(ID_ATTR);
  const undo = () => {
    if (before == null) span.removeAttribute('style'); else span.setAttribute('style', before);
    if (img) {
      if (imgBefore == null) img.removeAttribute('style'); else img.setAttribute('style', imgBefore);
    }
    if (hadOffset == null) span.removeAttribute(OFFSET_ATTR); else span.setAttribute(OFFSET_ATTR, hadOffset);
    if (hadId == null) span.removeAttribute(ID_ATTR); else span.setAttribute(ID_ATTR, hadId);
  };

  // Every piece of one image carries the same id. Set before the copy is
  // taken, so the copy has it; the copy is taken before the crop touches
  // anything, so it keeps the image's own margins and attributes.
  if (hadId == null) span.setAttribute(ID_ATTR, newBleedId());
  const rest = block.cloneNode(true);

  cropPiece(span, offset, remaining);
  const over = body.scrollHeight - body.clientHeight;
  const visible = Math.floor(remaining - over);
  if (over < 1 || visible < MIN_PIECE_PX) {
    undo();
    return null;
  }
  cropPiece(span, offset, visible);

  rest.setAttribute(CONT_ATTR, 'true');
  const restSpan = imageIn(rest);
  cropPiece(restSpan, offset + visible, remaining - visible);
  // The rest starts flush at the top of its page, and does not inherit the
  // selection outline the editor may have drawn on the piece it was copied from.
  restSpan.style.marginTop = '0px';
  restSpan.style.outline = '';
  return rest;
}

/**
 * Whether a cut on this page is stale: some piece here that does not end its
 * image would now be cut taller, because the content above it got shorter.
 *
 * A piece that does not end its image always reaches the page bottom. For a
 * wrapped image the first piece is not the last block on its page — the text
 * beside it follows it — so every piece on the page is checked, wherever it
 * sits. Measured exactly the way `splitBleedImage` measures: show the rest of
 * the image, take the overflow off, and compare with what is shown. More than
 * 1px of difference (the cut's rounding) means the cut is stale. The page is
 * left exactly as it was.
 *
 * @param {Element} body a .rv-page-body
 * @returns {boolean}
 */
export function bleedPieceHasRoom(body) {
  const pieces = Array.from(body.querySelectorAll(`${IMAGE_SEL}[${OFFSET_ATTR}]`));
  for (const piece of pieces) {
    const shown = parseFloat(piece.style.height) || 0;
    const offset = offsetOf(piece);
    const remaining = fullHeight(piece) - offset;
    if (shown >= remaining) continue; // the image ends on this page
    const before = piece.style.height;
    piece.style.height = `${remaining}px`;
    const over = body.scrollHeight - body.clientHeight;
    piece.style.height = before;
    if (Math.floor(remaining - over) > shown + 1) return true;
  }
  return false;
}

// The direct child of a .rv-page-body that holds `el`, or null.
export const blockOf = (el) => {
  let b = el;
  while (b?.parentElement && !b.parentElement.classList?.contains('rv-page-body')) b = b.parentElement;
  return b?.parentElement ? b : null;
};

/**
 * The pieces of the image `span` belongs to, first piece first. Found by id,
 * so it works for a wrapped image whose pieces have text between them; starts
 * from the nearest first piece before `span`, so a pasted copy sharing the id
 * is not mistaken for this image. A span that is not a piece gives itself.
 *
 * @param {Element} span a .rv-resizable-image, any piece of it
 * @param {Element} root the editor host
 * @returns {Element[]}
 */
export function bleedPiecesOf(span, root) {
  const id = span.getAttribute(ID_ATTR);
  if (!id || !span.hasAttribute(OFFSET_ATTR)) return [span];
  const all = Array.from(root.querySelectorAll(`${IMAGE_SEL}[${ID_ATTR}]`)).filter(
    (s) => s.getAttribute(ID_ATTR) === id && s.hasAttribute(OFFSET_ATTR)
  );
  const i = all.indexOf(span);
  if (i < 0) return [span];

  // A piece belongs to the chain only if it starts where the one before it
  // ends. That is what tells a stray copy (pasted before the next reflow)
  // from a real piece: same id, but its offset does not follow on.
  const shownOf = (s) => parseFloat(s.style.height) || 0;
  const continues = (a, b) => Math.abs(offsetOf(a) + shownOf(a) - offsetOf(b)) < 1;

  let first = span;
  let k = i;
  for (let j = i - 1; j >= 0 && offsetOf(first) > 0; j--) {
    if (continues(all[j], first)) { first = all[j]; k = j; }
  }
  if (offsetOf(first) > 0) return [span]; // no first piece: a stray copy

  const out = [first];
  for (let j = k + 1; j < all.length; j++) {
    if (offsetOf(all[j]) === 0) break; // another first piece: another image
    if (continues(out[out.length - 1], all[j])) out.push(all[j]);
  }
  return out.includes(span) ? out : [span];
}

/**
 * Take the id off every image that is whole again. Run after a reflow: an
 * image that was cut and now fits, or was switched off, keeps no trace.
 *
 * @param {Element} root the editor host
 */
export function dropStaleBleedIds(root) {
  root.querySelectorAll(`${IMAGE_SEL}[${ID_ATTR}]`).forEach((s) => {
    if (!s.hasAttribute(OFFSET_ATTR)) s.removeAttribute(ID_ATTR);
  });
}

/**
 * Turn pasted pieces of a running image into whole images before they land.
 *
 * Copying across a cut copies the crop, the offset and the id with it. Left
 * alone, a pasted first piece would share the original's id and a pasted later
 * piece would be thrown away as a copy at the next reflow. Uncropped and with
 * no id, each is simply an image again; one with `data-bleed` still on runs on
 * by itself when the reflow reaches it.
 *
 * @param {Element} root the parsed clipboard content
 */
export function releasePastedPieces(root) {
  root.querySelectorAll(`${IMAGE_SEL}[${OFFSET_ATTR}], ${IMAGE_SEL}[${ID_ATTR}]`).forEach((s) => {
    // A later piece had its top margin taken off to sit flush on its page;
    // give a non-wrapped one back the 12px the insert dialog gives every image.
    if (offsetOf(s) > 0 && !isWrapped(s)) s.style.marginTop = '12px';
    uncrop(s);
    s.removeAttribute(ID_ATTR);
    s.removeAttribute(CONT_ATTR);
  });
  root.querySelectorAll(`[${CONT_ATTR}]`).forEach((el) => el.removeAttribute(CONT_ATTR));
}

/** The first piece of the image `span` belongs to (the image itself). */
export function firstPieceOf(span, root) {
  return bleedPiecesOf(span, root)[0];
}

/**
 * Every block holding a piece of the image `span` belongs to, first piece
 * first. A span that is not a piece gives just its own block; a span outside
 * any page gives nothing.
 *
 * @param {Element} span a .rv-resizable-image, any piece of it
 * @param {Element} root the editor host
 * @returns {Element[]}
 */
export function bleedBlocksOf(span, root) {
  if (!blockOf(span)) return [];
  const out = [];
  for (const piece of bleedPiecesOf(span, root)) {
    const b = blockOf(piece);
    if (b && !out.includes(b)) out.push(b);
  }
  return out;
}
